import React from "react";
import GBPFlagImg from "asset/images/GB.svg";
import EURFlagImg from "asset/images/EU.svg";
import NGNFlagImg from "asset/images/Currency.svg";
import USFlagImg from "asset/images/US.png";
import USDCIcon from "asset/icons/USDC.svg";
import USDTIcon from "asset/icons/USDT.svg";
import KESIcon from "asset/icons/KES.svg";
import GHSIcon from "asset/icons/GH.svg";
import {
    WalletReceipt,
    WalletSend,
    WalletShield,
    WalletTimer,
} from "asset/icons";
import useOpeningFees from "hooks/useOpeningFees";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "component/ui/Dialog";
import { Button } from "component/ui/Button";
import Loader from "component/loader";
import { currencyToDisplay } from "../flex/utils";
import { BiSolidInfoCircle } from "react-icons/bi";

export const WALLET_CURRENCY_METADATA = {
    GBP: {
        name: "GBP Wallet",
        currencyName: "British Pounds",
        description:
            "This wallet includes a verified bank account, enabling you to receive British Pounds directly through your account number.",
        flag: GBPFlagImg,
        location: "the UK",
        fee: "£5.00",
        feeAmount: "£5.00",
        approxDeduct: "₦ 9,850.00 ~ £5.00",
    },
    EUR: {
        name: "EUR Wallet",
        currencyName: "Euros",
        description:
            "This wallet includes a verified bank account to receive Euros in the EU zone.",
        flag: EURFlagImg,
        location: "the EU",
        fee: "€5.00",
        feeAmount: "€5.00",
        approxDeduct: "₦ 8,250.00 ~ €5.00",
    },
    NGN: {
        name: "NGN Wallet",
        currencyName: "Naira",
        description:
            "Receive and send Naira seamlessly within Nigeria through your bank-linked wallet.",
        flag: NGNFlagImg,
        location: "Nigeria",
        fee: "₦ 500",
        feeAmount: "₦ 500",
        approxDeduct: "₦ 500.00",
    },
    USD: {
        name: "USD Wallet",
        currencyName: "US Dollars",
        description: "A secure bank account so you can send and receive US Dollars.",
        flag: USFlagImg,
        location: "the US",
        fee: null,
        feeAmount: "",
        approxDeduct: "",
    },
    KES: {
        name: "KES Wallet",
        currencyName: "Kenyan Shillings",
        description:
        "This wallet includes a verified bank account, enabling you to receive Kenyan Shillings directly through your account number.",
        flag: KESIcon,
        location: "Kenya",
        fee: "KSh 50",
        feeAmount: "KSh 50",
        approxDeduct: "₦ 2,109.35 ~ KSh 50.00",
    },
    GHS: {
        name: "GHS Wallet",
        currencyName: "Ghana Cedis",
        description:
            "This wallet includes a verified bank account, enabling you to receive Ghana Cedis directly through your account number.",
        flag: GHSIcon,
        location: "Ghana",
        fee: "GH₵ 5",
        feeAmount: "GH₵ 5",
        approxDeduct: "₦ 1,850.00 ~ GH₵ 5.00",
    },
    USDC: {
        name: "USDC Wallet",
        currencyName: "USDC",
        description:
        "This wallet allows you to receive USDC via multiple networks.",
        flag: USDCIcon,
        location: "the US",
        fee: null,
        feeAmount: "",
        approxDeduct: "",
    },
    USDT: {
        name: "USDT Wallet",
        currencyName: "USDT",
        description:
            "This wallet allows you to receive USDT via fiat or crypto across multiple networks.",
        flag: USDTIcon,
        location: "the US",
        fee: null,
        feeAmount: "",
        approxDeduct: "",
    },
};

export const AccountOpeningFeeBanner = ({
    fee,
    children,
    showIcon = true,
    className = "",
}) => {
  if (!fee && !children) return null;
    return (
      <div className={`-mx-6 px-6 bg-[#EBE4FF] flex items-center gap-2 h-10 ${className}`}>
            {showIcon && <BiSolidInfoCircle size={20} color="#6F00FF" />}
        <div className="text-xs md:text-sm text-[#2E0077] font-normal">
                {children || (
                    <>
              You will be charged an account opening fee of ~ {fee} for this wallet.
                    </>
                )}
            </div>
        </div>
    );
};

export const WalletStepIndicator = ({ currentStep = 1, totalSteps = 2, className = "" }) => {
    return (
        <div className={`flex items-center gap-1.5 ${className}`}>
            {Array.from({ length: totalSteps }).map((_, idx) => {
                const stepNum = idx + 1;
                const isActive = currentStep === stepNum;
                return (
                    <div
                        key={stepNum}
                        className={`h-2 !rounded-[2px] transition-all duration-300 ${
                            isActive ? "w-7 bg-[#6F00FF]" : "w-7 bg-[#EAE5FA]"
                        }`}
                    />
                );
            })}
        </div>
    );
};

const OpenWalletInfoModal = ({
    isOpen,
    currencyCode = "KES",
    onClose,
    onContinue,
    onSubmit,
    isLoading = false,
    step = 1,
    totalSteps = 2,
    customFee,
}) => {
  const { openingFees, isLoading: isLoadingOpeningFees } = useOpeningFees();

    if (!isOpen || !currencyCode) return null;

    const normalizedCode = String(currencyCode).toUpperCase();
    const isUSD = normalizedCode === "USD";
    const isCrypto = ["USDC", "USDT"].includes(normalizedCode);
    const meta = WALLET_CURRENCY_METADATA[normalizedCode] || {
        name: `${normalizedCode} Wallet`,
        currencyName: normalizedCode,
        description: `This wallet includes a verified bank account, enabling you to receive ${normalizedCode} directly through your account number.`,
        flag: USFlagImg,
        location: normalizedCode,
        fee: null,
        feeAmount: "",
        approxDeduct: "",
    };

    const feeItem = Array.isArray(openingFees)
        ? openingFees.find(
              (item) =>
                  String(item?.currency || "").toUpperCase() ===
                  normalizedCode
          )
        : null;

  const dynamicFee = (feeItem?.amount !== undefined ? `${currencyToDisplay(feeItem?.currency || meta.currencyName, feeItem.amount)}` : null);
  const feeToDisplay = dynamicFee || '';
  const hasOpeningFee = Boolean(feeToDisplay);
    const isMultiStep = totalSteps > 1;

    const handlePrimaryClick = () => {
        if (onContinue) onContinue();
        else if (onSubmit) onSubmit();
    };

    const features = isCrypto
        ? [
              {
                  icon: WalletShield,
                  text: "Your funds are securely held in a custodial wallet.",
              },
              {
                  icon: WalletSend,
                  text: "Use for fiat and crypto transfers around the world.",
              },
              {
                  icon: WalletTimer,
                  text: "Transfers take just a few seconds or up to a few minutes.",
              },
              {
                  icon: WalletReceipt,
                  text: "You can attach an invoice or order to your transactions.",
              },
          ]
        : [
              {
                  icon: WalletShield,
                  text: "Your funds are securely held in a licensed bank",
              },
              {
                  icon: WalletSend,
                  text: `Use for regular bank transfers within and outside ${meta.location || "{{location}}"}`,
              },
              {
                  icon: WalletTimer,
                text: "Transfers are completed instantly or within 24 hours",
              },
              {
                  icon: WalletReceipt,
                  text: isUSD
                      ? "You can attach an invoice or order to your transactions."
                      : "Transfers to other currencies require an invoice or order",
              },
          ];

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
            <DialogContent
                showCloseButton={true}
          className="max-w-[480px] sm:max-w-[560px] flex flex-col  gap-3 rounded-[18px] sm:rounded-[20px] bg-white border-0  shadow-2xl overflow-hidden"
        >
          <div className="pt-6 px-6 flex flex-col gap-3">
            <div>
                {/* Header Title */}
                <DialogTitle className="text-[20px] sm:text-[22px] font-bold text-[#101828] tracking-tight">
                   Open a new wallet
                </DialogTitle>
                <DialogDescription className="sr-only">
                    Details and features for opening a new {meta.name}
                </DialogDescription>
            </div>

                {/* Purple Banner Card */}
                <div
                    className="rounded-[8px] p-4 text-white relative overflow-hidden bg-gradient-to-r from-[#5B00E8] via-[#6500F0] to-[#7200FF]"
                    style={{
                        backgroundImage: `url('/walletBG.png')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                >
                    <div className="flex items-center gap-3">
                        <img
                            src={meta.flag}
                            alt={meta.name}
                            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover shadow-sm bg-white/10 shrink-0"
                        />
                        <h3 className="font-bold text-[19px] sm:text-[21px] text-white tracking-tight">
                            {meta.name}
                        </h3>
                    </div>
                    <p className="text-[13px] sm:text-[14px] text-white/95 mt-2.5 font-normal leading-relaxed">
                        {meta.description}
                    </p>
                </div>

                {/* Features list */}
            <div className="flex flex-col gap-4 py-4">
                    {features.map(({ icon: Icon, text }, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                            <Icon className="size-5 text-[#A173FF] shrink-0 mt-0.5" />
                            <span className="text-[14px] sm:text-[14.5px] font-normal text-[#101828]">
                                {text}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Account opening fee banner (Edge-to-Edge full width strip) */}
                {hasOpeningFee && <AccountOpeningFeeBanner fee={feeToDisplay} />}
          </div>

                {/* Footer with Stepper & Action buttons */}
          <div style={{ borderTop: "1px solid #F2F3FC" }} className="flex p-3 items-center justify-between">
                    {isMultiStep ? (
                        <WalletStepIndicator currentStep={step} totalSteps={totalSteps} />
                    ) : (
                        <div />
                    )}

                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            onClick={onClose}
                            className="text-[#344054] hover:text-[#101828] hover:bg-transparent font-medium text-[14.5px] px-3 py-2"
                        >
                            Back
                        </Button>
                        <Button
                            onClick={handlePrimaryClick}
                            disabled={isLoading || isLoadingOpeningFees}
                            className="bg-[#6F00FF] hover:bg-[#5E00DB] text-white font-semibold text-[14.5px] px-6 py-2.5 rounded-[8px] shadow-none transition-all"
                        >
                            {isLoading || isLoadingOpeningFees ? (
                                <Loader />
                            ) : (
                                "Continue"
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default OpenWalletInfoModal;
